import type { TallyExportModule } from "./tally-export-preview";

export type TallyNarrationFieldDefinition = {
  key: string;
  label: string;
  defaultChecked: boolean;
};

export type TallyNarrationOptions = Record<string, boolean>;

export function getTallyNarrationFieldDefinitions(
  module: TallyExportModule
): TallyNarrationFieldDefinition[] {
  switch (module) {
    case "lagat":
      return [
        { key: "includeItsNo", label: "ITS Number", defaultChecked: true },
        { key: "includeName", label: "Name", defaultChecked: true },
        { key: "includeAmount", label: "Amount", defaultChecked: true },
        { key: "includePaymentMode", label: "Payment Mode", defaultChecked: true },
        { key: "includeTransactionId", label: "Transaction ID / Ref", defaultChecked: true },
        { key: "includePurpose", label: "Purpose", defaultChecked: true },
      ];
    case "sabil":
      return [
        { key: "includeItsNo", label: "ITS Number", defaultChecked: true },
        { key: "includeAmount", label: "Amount", defaultChecked: true },
        { key: "includePaymentMode", label: "Payment Mode", defaultChecked: true },
        { key: "includeTransactionId", label: "Transaction ID / Ref", defaultChecked: true },
        { key: "includeName", label: "Name / Firm", defaultChecked: true },
        { key: "includeTakhmeen", label: "Takhmeen Details", defaultChecked: true },
      ];
    case "rent":
      return [
        { key: "includeItsNo", label: "Organiser ITS", defaultChecked: true },
        { key: "includeName", label: "Organiser Name", defaultChecked: true },
        { key: "includeAmount", label: "Amount", defaultChecked: true },
        { key: "includePaymentMode", label: "Payment Mode", defaultChecked: true },
        { key: "includeTransactionId", label: "Transaction ID / Ref", defaultChecked: true },
        { key: "includeHall", label: "Hall Name", defaultChecked: true },
        { key: "includePurpose", label: "Purpose", defaultChecked: true },
        { key: "includeBookingDate", label: "Booking Date", defaultChecked: true },
      ];
    case "niyaaz":
      return [
        { key: "includeItsNo", label: "HOF ITS", defaultChecked: true },
        { key: "includeName", label: "HOF Name", defaultChecked: true },
        { key: "includeAmount", label: "Amount", defaultChecked: true },
        { key: "includePaymentMode", label: "Payment Mode", defaultChecked: true },
        { key: "includeTransactionId", label: "Transaction ID / Ref", defaultChecked: true },
        { key: "includeFormNo", label: "Form No", defaultChecked: true },
        { key: "includeEvent", label: "Event / Markaz", defaultChecked: true },
      ];
    case "yearlyNiyaaz":
      return [
        { key: "includeItsNo", label: "ITS Number", defaultChecked: true },
        { key: "includeName", label: "Name", defaultChecked: true },
        { key: "includeAmount", label: "Amount", defaultChecked: true },
        { key: "includePaymentMode", label: "Payment Mode", defaultChecked: true },
        { key: "includeTransactionId", label: "Transaction ID / Ref", defaultChecked: true },
        { key: "includeFormNo", label: "Form No", defaultChecked: true },
        { key: "includeYear", label: "Hijri Year", defaultChecked: true },
      ];
    case "fmb":
      return [
        { key: "includeItsNo", label: "ITS Number", defaultChecked: true },
        { key: "includeName", label: "Name", defaultChecked: true },
        { key: "includeAmount", label: "Amount", defaultChecked: true },
        { key: "includePaymentMode", label: "Payment Mode", defaultChecked: true },
        { key: "includeTransactionId", label: "Transaction ID / Ref", defaultChecked: true },
        { key: "includeTarget", label: "Target (Annual/Contrib)", defaultChecked: true },
      ];
    default:
      return [];
  }
}

export function getDefaultNarrationOptions(module: TallyExportModule): TallyNarrationOptions {
  const defs = getTallyNarrationFieldDefinitions(module);
  const options: TallyNarrationOptions = {};
  for (const def of defs) {
    options[def.key] = def.defaultChecked;
  }
  return options;
}

const STORAGE_PREFIX = "tally_narration_options_";

export function getStoredNarrationOptions(module: TallyExportModule): TallyNarrationOptions {
  const defaults = getDefaultNarrationOptions(module);
  if (typeof window === "undefined" || !window.localStorage) {
    return defaults;
  }
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${module}`);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      // Merge with defaults in case new fields were added
      return { ...defaults, ...parsed };
    }
  } catch {
    // Ignore storage parse errors
  }
  return defaults;
}

export function setStoredNarrationOptions(
  module: TallyExportModule,
  options: TallyNarrationOptions
): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${module}`, JSON.stringify(options));
  } catch {
    // Ignore storage errors
  }
}
